import { memo, forwardRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  type MenuProps,
  type SxProps,
  type Theme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { bloxValues } from 'themes/constants';
import type { Campaign } from '../hooks/useCampaignHandlers';
import type { GenericSearchItem } from 'components/Search/components/SearchItemsProvider';
import { useSearchParams } from 'react-router-dom';

export type MuiCloseEvent = Partial<Parameters<NonNullable<MenuProps['onClose']>>[0]>;
export type MuiCloseReasons = Partial<Parameters<NonNullable<MenuProps['onClose']>>[1]>;

export interface CampaignMiniMenuConfig {
  edit?: { enabled?: boolean };
  duplicate?: { enabled?: boolean };
  viewLogs?: { enabled?: boolean };
}

export const allCampaignMenuOptions: CampaignMiniMenuConfig = {
  edit: { enabled: true },
  duplicate: { enabled: true },
  viewLogs: { enabled: true },
};

export interface CampaignMiniMenuProps {
  menuEvent: React.MouseEvent<HTMLElement> | null;
  setMenuEvent?: (event: React.MouseEvent<HTMLElement> | null) => void;
  selectedItems: GenericSearchItem[];
  setSelectedItems?: (items: GenericSearchItem[]) => void;
  handleMiniMenuClose: () => void;
  clearSelectedItems?: () => void;
  menuConfig?: CampaignMiniMenuConfig;
  contextMenuItem?: GenericSearchItem | null;
  isContextItemDisabled?: boolean;
  anchorEl?: HTMLElement | null;
  onEdit?: (campaign: Campaign) => void;
  onDuplicate?: (campaign: Campaign) => void;
}

type ButtonState = {
  show: boolean;
  disabled: boolean;
  info?: string;
};

export const CampaignMiniMenu = memo(
  forwardRef<HTMLDivElement, CampaignMiniMenuProps>(
    (
      {
        menuEvent,
        selectedItems,
        handleMiniMenuClose,
        clearSelectedItems,
        menuConfig = allCampaignMenuOptions,
        contextMenuItem,
        isContextItemDisabled,
        onEdit,
        onDuplicate,
        anchorEl,
      },
      ref,
    ) => {
      const theme = useTheme();
      const { t } = useTranslation();
      const [searchParams, setSearchParams] = useSearchParams();

      // Determine menu mode: button (anchorEl) or context (menuEvent position)
      const isButtonMode = Boolean(anchorEl);

      // Menu UI States
      const menuX = menuEvent?.clientX ?? undefined;
      const menuY = menuEvent?.clientY ?? undefined;
      const hasXY = menuX && menuY;
      const isOpen = isButtonMode ? Boolean(anchorEl) : Boolean(hasXY);
      const anchorPos: MenuProps['anchorPosition'] = hasXY
        ? { top: menuY, left: menuX }
        : undefined;

      // Determine which items to use for menu operations
      const hasSelections = Boolean(selectedItems?.length);
      const contextItemId = contextMenuItem?.id;
      const isContextItemSelected =
        hasSelections && contextItemId
          ? selectedItems.some((item) => item?.id === contextItemId)
          : false;

      // Right click behaviors:
      // 1. If items are selected AND the right-clicked item is one of them -> use all selected items (multi-item mode)
      // 2. If items are selected BUT the right-clicked item is NOT selected -> use only the right-clicked item (single-item mode)
      // 3. If no items are selected -> use the right-clicked item (single-item mode)
      const effectiveItems = useMemo(
        () =>
          hasSelections && isContextItemSelected
            ? selectedItems
            : contextMenuItem
              ? [contextMenuItem]
              : [],
        [hasSelections, isContextItemSelected, selectedItems, contextMenuItem],
      );

      const hasMultipleSelected = Boolean(effectiveItems?.length > 1);
      // Cast to Campaign for type safety - items come from campaign search grid
      const lastItem: Campaign | undefined =
        effectiveItems?.length > 0
          ? (effectiveItems[effectiveItems.length - 1] as Campaign)
          : undefined;
      const logsCampaignId = lastItem?.uuid ?? lastItem?.id;

      // --------------------------------------------------
      // Styles
      // --------------------------------------------------
      const iconSx: SxProps<Theme> = useMemo(() => {
        return {
          fontSize: bloxValues.iconSizeRegular,
          color: theme.palette.text.secondary,
          mr: 1,
        };
      }, [theme]);

      const menuSx: SxProps<Theme> = useMemo(() => {
        return {
          '& .MuiMenuItem-root': {
            fontSize: bloxValues.regularFontSizeRem,
          },
        };
      }, []);

      const internalOnClose = useCallback(
        (event?: MuiCloseEvent, reason?: MuiCloseReasons) => {
          if (event && 'stopPropagation' in event) {
            (event as Event).stopPropagation();
            (event as Event).preventDefault();
          }
          handleMiniMenuClose();
        },
        [handleMiniMenuClose],
      );

      // --------------------------------------------------
      // Edit Button
      // --------------------------------------------------
      const editState: ButtonState = useMemo(() => {
        const showEdit = Boolean(menuConfig?.edit?.enabled);
        const canEdit = Boolean(lastItem?.id && !hasMultipleSelected);

        if (!canEdit) {
          return {
            disabled: true,
            show: showEdit,
            info: hasMultipleSelected
              ? t(
                  'blox.email_reach.campaign.mini_menu.edit_disabled_multiple',
                  'Cannot edit multiple campaigns at once',
                )
              : undefined,
          };
        }

        return {
          disabled: false,
          show: showEdit,
        };
      }, [hasMultipleSelected, lastItem, menuConfig?.edit?.enabled, t]);

      const handleEdit = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          event?.stopPropagation();
          if (editState?.disabled || !lastItem) return;
          onEdit?.(lastItem);
          internalOnClose();
        },
        [editState, lastItem, onEdit, internalOnClose],
      );

      // --------------------------------------------------
      // Duplicate Button
      // --------------------------------------------------
      const duplicateState: ButtonState = useMemo(() => {
        const showDuplicate = Boolean(menuConfig?.duplicate?.enabled);
        const canDuplicate = Boolean(lastItem?.id && !hasMultipleSelected);

        if (!canDuplicate) {
          return {
            disabled: true,
            show: showDuplicate,
            info: hasMultipleSelected
              ? t(
                  'blox.email_reach.campaign.mini_menu.duplicate_disabled_multiple',
                  'Cannot duplicate multiple campaigns at once',
                )
              : undefined,
          };
        }

        return {
          disabled: false,
          show: showDuplicate,
        };
      }, [hasMultipleSelected, lastItem?.id, menuConfig?.duplicate?.enabled, t]);

      const handleDuplicate = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          event?.stopPropagation();
          if (duplicateState?.disabled || !lastItem) {
            return;
          }
          onDuplicate?.(lastItem);
          internalOnClose();
        },
        [duplicateState, lastItem, onDuplicate, internalOnClose],
      );

      // --------------------------------------------------
      // View Logs Button
      // --------------------------------------------------
      const viewLogsState: ButtonState = useMemo(() => {
        const showViewLogs = Boolean(menuConfig?.viewLogs?.enabled);
        const canViewLogs = Boolean(logsCampaignId && !hasMultipleSelected);

        if (!canViewLogs) {
          return {
            disabled: true,
            show: showViewLogs,
            info: hasMultipleSelected
              ? t(
                  'blox.email_reach.campaign.mini_menu.view_logs_disabled_multiple',
                  'Cannot view logs for multiple campaigns at once',
                )
              : undefined,
          };
        }

        return {
          disabled: false,
          show: showViewLogs,
        };
      }, [hasMultipleSelected, logsCampaignId, menuConfig?.viewLogs?.enabled, t]);

      const handleViewLogs = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event?.preventDefault();
          event?.stopPropagation();
          if (viewLogsState?.disabled || !logsCampaignId) {
            return;
          }

          const next = new URLSearchParams(searchParams);
          next.set('viewLogs', logsCampaignId);
          setSearchParams(next, { replace: false });
          internalOnClose();
        },
        [
          internalOnClose,
          logsCampaignId,
          searchParams,
          setSearchParams,
          viewLogsState?.disabled,
        ],
      );

      // --------------------------------------------------
      // Render
      // --------------------------------------------------
      return (
        <>
          <Menu
            ref={ref}
            open={isOpen}
            onClose={internalOnClose}
            anchorReference={isButtonMode ? 'anchorEl' : 'anchorPosition'}
            anchorEl={isButtonMode ? anchorEl : undefined}
            anchorPosition={!isButtonMode ? anchorPos : undefined}
            sx={menuSx}
          >
            {/* Edit */}
            {editState.show && (
              <Tooltip
                title={editState?.info || ''}
                placement="left"
                arrow
                disableHoverListener={!editState.disabled || !editState.info}
              >
                <span>
                  <MenuItem
                    data-testid="campaign-edit-menu-item"
                    onClick={handleEdit}
                    disabled={editState.disabled}
                  >
                    <ListItemIcon>
                      <EditIcon sx={iconSx} />
                    </ListItemIcon>
                    <ListItemText>
                      {t('blox.email_reach.campaign.mini_menu.edit')}
                    </ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
            )}

            {/* Duplicate */}
            {duplicateState.show && (
              <Tooltip
                title={duplicateState?.info || ''}
                placement="left"
                arrow
                disableHoverListener={!duplicateState.disabled || !duplicateState.info}
              >
                <span>
                  <MenuItem
                    data-testid="campaign-duplicate-menu-item"
                    onClick={handleDuplicate}
                    disabled={duplicateState.disabled}
                  >
                    <ListItemIcon>
                      <ContentCopyIcon sx={iconSx} />
                    </ListItemIcon>
                    <ListItemText>
                      {t('blox.email_reach.campaign.mini_menu.duplicate')}
                    </ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
            )}

            {/* View Logs */}
            {viewLogsState.show && (
              <Tooltip
                title={viewLogsState?.info || ''}
                placement="left"
                arrow
                disableHoverListener={!viewLogsState.disabled || !viewLogsState.info}
              >
                <span>
                  <MenuItem
                    data-testid="campaign-view-logs-menu-item"
                    onClick={handleViewLogs}
                    disabled={viewLogsState.disabled}
                  >
                    <ListItemIcon>
                      <ReceiptLongIcon sx={iconSx} />
                    </ListItemIcon>
                    <ListItemText>
                      {t('blox.email_reach.campaign.logs.action')}
                    </ListItemText>
                  </MenuItem>
                </span>
              </Tooltip>
            )}
          </Menu>
        </>
      );
    },
  ),
);

CampaignMiniMenu.displayName = 'CampaignMiniMenu';
